// Service-worker registration + update detection.
//
// A new worker sits in `waiting` until every window closes — and home-screen
// PWAs rarely fully close, so users can sit on a stale build for days. We detect
// the waiting worker and surface a "new version" toast; only when the user taps
// do we tell it to skipWaiting and reload. Never skipWaiting unprompted — it can
// hand a live session mismatched asset chunks mid-flight.

let waitingWorker: ServiceWorker | null = null;

export async function registerServiceWorker(onUpdateReady: () => void): Promise<void> {
  if (!("serviceWorker" in navigator)) return;

  try {
    // updateViaCache:"none" — never serve sw.js itself from the HTTP cache, so a
    // shipped fix is always picked up even if the static host caches aggressively.
    const reg = await navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });

    // Already one waiting (e.g. registered on a previous load).
    if (reg.waiting && navigator.serviceWorker.controller) {
      waitingWorker = reg.waiting;
      onUpdateReady();
    }

    reg.addEventListener("updatefound", () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          waitingWorker = installing;
          onUpdateReady();
        }
      });
    });

    // When the new worker takes control, reload once to get matching assets.
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  } catch (err) {
    console.error("[pwa] service worker registration failed", err);
  }
}

/** User tapped "reload" on the update toast. */
export function applyUpdate(): void {
  waitingWorker?.postMessage({ type: "SKIP_WAITING" });
}
